import React, { ReactNode, useState, useEffect, useRef } from "react";
import Sidebar from "./sidebar";
import Header from "./header";
import { Layout } from "antd";
import DataProvider from "../../services";
import { getEmptyFields, isProfileCompletionDue } from "../../lib/profile-completion";
import type { ApiProfile } from "../../lib/profile-completion";
import { ProfileCompleteModal } from "../profile-completion/ProfileCompleteModal";

type Props = {
  children: ReactNode;
};

export default function MainLayout({ children }: Props) {
  const { Content } = Layout;
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [profile, setProfile] = useState<ApiProfile | null>(null);
  const [profileCompletionOpen, setProfileCompletionOpen] = useState(false);
  const profileCompletionEvaluatedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await DataProvider.getProfile();
        if (cancelled) return;
        const data = res?.data ?? res;
        setProfile((data as ApiProfile) ?? null);
      } catch {
        if (!cancelled) setProfile(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!profile) return;
    // Only evaluate once per mount - a later profile update (e.g. from the
    // modal's own save) shouldn't re-trigger this check.
    if (profileCompletionEvaluatedRef.current) return;
    profileCompletionEvaluatedRef.current = true;

    const empty = getEmptyFields(profile);
    if (empty.length > 0 && isProfileCompletionDue()) {
      setProfileCompletionOpen(true);
    }
  }, [profile]);

  const handleProfileCompletionDismiss = () => {
    profileCompletionEvaluatedRef.current = true;
    setProfileCompletionOpen(false);
  };

  return (
    <Layout className="h-screen flex lg:justify-between justify-center">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <Content
        className={` ${
          collapsed ? "w-full" : "lg:w-[80%]"
        } flex flex-col w-full h-full bg-gray-50`}
      >
        <div className="h-24 fixed z-50 w-full border-b border-gray-300 border-solid">
          <Header collapsed={collapsed} />
        </div>
        <div className="flex-1 overflow-y-auto md:p-4 p-2 mt-24">
          {children}
        </div>
      </Content>
      <ProfileCompleteModal
        open={profileCompletionOpen}
        onOpenChange={setProfileCompletionOpen}
        profile={profile}
        onDismissSession={handleProfileCompletionDismiss}
        onProfileUpdated={(updated) => setProfile(updated)}
      />
    </Layout>
  );
}
